import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Leaf, ArrowLeft, Mail, Lock, Key, CheckCircle, Eye, EyeOff } from "lucide-react";

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userType = searchParams.get("type") || "user";
  
  const [step, setStep] = useState("email"); // email, code, success
  const [email, setEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error("Please enter your email");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset/request`, {
        email,
        user_type: userType
      });
      
      setStep("code");
      toast.success("Reset code sent! Check your email.");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!resetCode) {
      toast.error("Please enter the reset code");
      return;
    }

    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset/verify`, {
        email,
        reset_code: resetCode,
        new_password: newPassword,
        user_type: userType
      });
      
      setStep("success");
      toast.success("Password reset successful!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid or expired reset code");
    } finally {
      setLoading(false);
    }
  };

  const getLoginPath = () => {
    return userType === "coach" ? "/coach-login" : "/login";
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col">
      {/* Header */}
      <header className="glass-header">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Leaf className="w-6 h-6 text-[#7C9A92]" />
            <span className="font-serif text-xl font-medium text-[#2C332B]">CoachBridge</span>
          </Link>
          <Link 
            to={getLoginPath()} 
            className="flex items-center gap-2 text-[#78716C] hover:text-[#2C332B] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Card className="border-stone-100 shadow-xl">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 rounded-2xl bg-[#7C9A92]/10 flex items-center justify-center mx-auto mb-4">
                {step === "success" ? (
                  <CheckCircle className="w-8 h-8 text-[#7C9A92]" />
                ) : (
                  <Key className="w-8 h-8 text-[#7C9A92]" />
                )}
              </div>
              <CardTitle className="font-serif text-3xl font-light text-[#2C332B]">
                {step === "success" ? "Password Reset!" : "Reset Password"}
              </CardTitle>
              <p className="text-[#78716C] mt-2">
                {step === "email" && "Enter your email to receive a reset code"}
                {step === "code" && "Enter the code and your new password"}
                {step === "success" && "Your password has been updated"}
              </p>
            </CardHeader>
            
            <CardContent className="pt-6">
              {step === "email" && (
                <form onSubmit={handleRequestCode} className="space-y-6">
                  <div className="space-y-2">
                    <Label className="text-[#2C332B] font-medium">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#78716C]" />
                      <Input
                        type="email"
                        placeholder={userType === "coach" ? "coach@example.com" : "you@example.com"}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="h-12 pl-12 rounded-xl border-stone-200 bg-stone-50/50"
                        data-testid="reset-email"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 rounded-xl bg-[#7C9A92] hover:bg-[#6B8A82] text-white"
                    data-testid="request-code-btn"
                  >
                    {loading ? "Sending..." : "Send Reset Code"}
                  </Button>
                </form>
              )}

              {step === "code" && (
                <form onSubmit={handleResetPassword} className="space-y-6">
                  <div className="p-3 bg-[#7C9A92]/10 border border-[#7C9A92]/20 rounded-xl">
                    <p className="text-sm text-[#2C332B]">
                      We've sent a 6-digit code to <strong>{email}</strong>. Check your inbox (and spam folder).
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#2C332B] font-medium">Reset Code</Label>
                    <Input
                      type="text"
                      placeholder="Enter 6-digit code"
                      value={resetCode}
                      onChange={(e) => setResetCode(e.target.value)}
                      className="h-12 rounded-xl border-stone-200 bg-stone-50/50 text-center text-xl tracking-widest"
                      maxLength={6}
                      data-testid="reset-code"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#2C332B] font-medium">New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#78716C]" />
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder="Min. 6 characters"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="h-12 pl-12 pr-12 rounded-xl border-stone-200 bg-stone-50/50"
                        data-testid="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-[#78716C] hover:text-[#2C332B]"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#2C332B] font-medium">Confirm New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#78716C]" />
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder="Confirm your password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="h-12 pl-12 rounded-xl border-stone-200 bg-stone-50/50"
                        data-testid="confirm-new-password"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 rounded-xl bg-[#7C9A92] hover:bg-[#6B8A82] text-white"
                    data-testid="reset-password-btn"
                  >
                    {loading ? "Resetting..." : "Reset Password"}
                  </Button>

                  <button
                    type="button"
                    onClick={() => setStep("email")}
                    className="w-full text-sm text-[#78716C] hover:text-[#2C332B]"
                  >
                    Didn't receive a code? Try again
                  </button>
                </form>
              )}

              {step === "success" && (
                <div className="text-center space-y-6">
                  <p className="text-[#78716C]">
                    You can now log in with your new password.
                  </p>
                  <Link to={getLoginPath()}>
                    <Button className="rounded-full px-8 bg-[#7C9A92] hover:bg-[#6B8A82]">
                      Go to Login
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
};

export default ForgotPasswordPage;
